<template>
	<div class="cs-notfound" :class="{ 'cs-notfound-container' : !isPage }">
		<div class="cs-notfound-content">
			<div
				v-if="isPage" 
				class="cs-notfound--header">
				<h1 
					tabindex="0"
					@keydown="onKeydownTitle">{{ errorPageTitle }}</h1>
				<cs-skip-link 
					v-if="$accessibilityMode()"
					:show="showSkipLink"
					:title="'Skip to homepage'"
					:url="'/'"
					@on-blur="showSkipLink = false" />
			</div>
			<div v-else class="cs-notfound-code-container">
				<span class="cs-notfound-code">
					<span class="cs-fg-primary">{{ status }}</span>
				</span>
				<img class="cs-notfound-image" :src="errorImage" :alt="$translateWithDefault('notFound.sadFace')" />
			</div>
			<div class="cs-notfound--body">
				<p 
					v-if="isPage" 
					v-html="errorPageMessage"
					class="cs-notfound-message"></p>
				<p 
					v-else
					class="cs-notfound-message">{{ $translateWithDefault('componentNotFoundMessage', errorMessage) }}</p>

				<div class="cs-notfound-homelink">
					<button 
						type="button" 
						class="cs-button cs-bg-primary" 
						:class="{ 'cs-bg-accent-accessible' : $accessibilityMode() }"
						:title="$translateWithDefault('notFound.homeLink')"
						@click="$router.replace('/')" >
						<font-awesome-icon :icon="['fal', 'home']" :title="$translateWithDefault('notFound.housePic')" />
						{{ $translateWithDefault('notFound.goHome') }}
					</button>
				</div>
			</div>
		</div>
	</div>
</template>
<script type="text/javascript">
	import MiComponentBase from '@/js/mixins/mi-componentBase';
	import MiDynamicComponentLoadingUtils from '@/js/mixins/mi-dynamicComponentLoadingUtils';

	/**
	 * not found component. Displayed whenever there is an error loading a dynamic component or the url requested cannot be found
	 * 
	 * @version 1.0.0
	 * @since 1.0.0
	 */
	export default {
		name : 'cs-notfound',

		mixins : [ 
			MiComponentBase,
			MiDynamicComponentLoadingUtils
		],

		components: {
			'cs-skip-link': () => import(/* webpackChunkName: "cs-skip-link" */ '@/js/components/shared/cs-skip-link')
		},

		props: {
			status: {
				type: Number,
				required: false,
				default: 404
			}
		},

		data() {
			return {
				message : 'The page you are trying to access cannot be found.',
				showSkipLink: false
			}
		},

		mounted() {
			// We're clearing app loading overlay when screen component finished loading
			this.$hideAppLoadingOverlay();
		},

		methods: {
			/**
			 * @public
			 * @description
			 * Shows Skip Link on Tab key
			 * 
			 * @param { Object } event the keyboard event object
			 * @returns void
			 */
			onKeydownTitle(event) {
				if ( event.key == 'Tab' && !this.showSkipLink ) this.showSkipLink = true;
			}
		},

		computed: {
			/**
			 * @public
			 * @description
			 * Returns an error image 
			 * @returns { String }
			 */
			errorImage() {
				switch(this.status) {
					case 401:
					case 403:
						return '/dist/images/401_angry_face.svg';
					case 404:
						return '/dist/images/404_sad_face.svg';
				}
			},

			/**
			 * @public
			 * @description
			 * Returns an error message 
			 * @returns { String }
			 */
			errorMessage() {
				switch(this.status) {
					case 401:
					case 403:
						return 'The page cannot be accessed.';
					case 404:
						return this.message;
				}
			},

			/**
			 * @public
			 * @description
			 * Returns an error page title 
			 * The title is defined in the Platform Instance Settings
			 * 
			 * @returns { String }
			 */
			errorPageTitle() {
				return this.$getErrorPageSettings().notFoundPageTitle;
			},
			
			/**
			 * @public
			 * @description
			 * Returns an error page message 
			 * The message is defined in the Platform Instance Settings
			 * 
			 * @returns { String }
			 */
			errorPageMessage() {
				return this.$getErrorPageSettings().notFoundPageContent;
			}
		}
	}
</script>
<style lang="scss">
	.cs-notfound {
		display: flex;
		flex: 1 1 auto;
		flex-direction: row;
		padding: $cs-standard-pad;

		&.cs-notfound-container {
			align-items: center;
			justify-content: center;
		}

		h1:focus {
			outline: none;;
		}

		.cs-notfound-message {
			font-family: $font-family-sans-serif;
			font-size: calc(1.275rem + 0.3vw);
			font-weight: 300;
			line-height: 1.2;
			margin-top: $cs-large-pad;

			@include media-breakpoint-up(lg) {
				margin-top: $cs-small-pad;
				font-size: 2.4rem;
			}
		}

		.cs-notfound-code-container {
			display: flex;
			align-items: center;
			justify-content: center;
		}

		.cs-notfound-homelink {
			text-align: center;
			margin-top: $cs-large-pad;
			
			.cs-button {
				font-size: 2rem;
				height: auto;
				line-height: 2;
			}
		}
		
		.cs-notfound-code {
			font-size: 4rem;
			line-height: 1;

			@include media-breakpoint-up(lg) {
				font-size: 10rem;
			}
		}

		.cs-notfound-image {
			flex: 1 1 auto;
			max-width: 120px;
			vertical-align: top;
			margin-left: $cs-large-pad;
			line-height: 6rem;

			@include media-breakpoint-up(lg) {
				line-height: 10rem;
				max-width: 240px;
			}
		}
	}
</style>
